import React, { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import axios from 'axios'
import moment from 'moment'
import Panel from '../ui/Panel'
import PlayerCard from '../ui/PlayerCard'
import Avatar, { initialsFrom } from '../ui/Avatar'
import './favourites.css'

/*
 * WATCHLIST ACTIVITY FEED
 *
 * One merged, newest-first stream of what the players a scout follows have been
 * doing — "Giannis M. uploaded a video", "Giannis M. entered Skill Challenge".
 *
 * Two requests per watched player:
 *   manager/dashboard/userposts/{uuid}       -> posts, stories, videos
 *   manager/dashboard/userchallenges/{uuid}  -> challenges the player entered
 *
 * A 50-player watchlist would be 100 requests, so the fetches run through a
 * six-lane pool instead of one Promise.all, and every uuid is cached for the
 * session: revisiting the page costs nothing.
 *
 * DATES — the one rule this file will not bend: an entry is only placed on the
 * timeline when the payload carries a real date for it. Posts do (DateCreated /
 * Timestamp). Challenge rows carry DateTo, which is when the *challenge closes*
 * — a property of the challenge, not a record of when this player entered it —
 * so it is never used as an activity time and never used for sorting. Undated
 * challenge entries are collected under a separate "Recent challenges" heading.
 */

/* uuid -> { posts, challenges }. Session-lived, survives remounts. */
const activityCache = new Map()

const LANES = 6          // parallel requests in flight, at most
const MAX_FEED = 40      // dated entries rendered
const MAX_UNDATED = 12   // undated challenge entries rendered
const SNIPPET = 120      // characters of description shown on a row

const auth = () => ({
  headers: { Authorization: 'Bearer ' + sessionStorage.getItem('mngapiToken') },
})

/* The API returns either a bare array or { results: [...] }. */
const rows = (payload) => {
  if (Array.isArray(payload)) return payload
  if (payload && Array.isArray(payload.results)) return payload.results
  return []
}

/* ------------------------------------------------------------------
   Dates. Returns a valid moment or null — never a guess.
   ------------------------------------------------------------------ */
const FORMATS = [moment.ISO_8601, 'YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD HH:mm', 'YYYY-MM-DD']

const toMoment = (value) => {
  if (value === null || value === undefined || value === '') return null

  const raw = String(value).trim()
  if (!raw || raw.indexOf('0000-00-00') === 0) return null

  let parsed
  if (/^\d+$/.test(raw)) {
    const n = Number(raw)
    if (!Number.isFinite(n) || n <= 0) return null
    // 10 digits is seconds, 13 is milliseconds.
    parsed = moment(n < 1e12 ? n * 1000 : n)
  } else {
    parsed = moment(raw, FORMATS)
  }

  if (!parsed.isValid()) return null
  // Reject epoch-zero placeholders and anything the future could not have produced.
  if (parsed.year() < 2000) return null
  if (parsed.isAfter(moment().add(2, 'day'))) return null
  return parsed
}

/* First field that yields a real date, in order of preference. */
const dateFrom = (row, fields) => {
  for (let i = 0; i < fields.length; i += 1) {
    const found = toMoment(row[fields[i]])
    if (found) return found
  }
  return null
}

const POST_DATE_FIELDS = ['DateCreated', 'Timestamp']

/* DateTo is deliberately NOT in this list — see the header note. These are the
   per-user fields the challenge payload may or may not carry; when none of them
   is present the entry goes to the undated group. */
const CHALLENGE_DATE_FIELDS = ['DateCreated', 'DateSubmitted', 'DateEntered', 'DateCompleted', 'Timestamp']

/* ------------------------------------------------------------------
   Sentences
   ------------------------------------------------------------------ */

/* "Giannis Makris" -> "Giannis M." */
export const shortName = (full) => {
  const parts = String(full || '').trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return 'This player'
  if (parts.length === 1) return parts[0]
  return parts[0] + ' ' + parts[parts.length - 1].charAt(0).toUpperCase() + '.'
}

/* "Giannis Makris" -> "GM".
   The monogram rule now lives in ui/Avatar so every face in the product spells a
   name the same way; this stays exported only so existing callers keep working. */
export const initialsOf = (name) => initialsFrom(name)

/* Photo arrives as a string or an array depending on the row; both answer .length. */
const hasMedia = (value) => value !== null && value !== undefined && String(value).length > 0

const clean = (text) => String(text || '').replace(/\s+/g, ' ').trim()

const snippet = (text) => {
  const flat = clean(text)
  if (flat.length <= SNIPPET) return flat
  return flat.slice(0, SNIPPET - 1).trimEnd() + '…'
}

const postKind = (post) => {
  if (hasMedia(post.VideoID)) return 'video'
  if (Number(post.IsStory) === 1) return 'story'
  if (hasMedia(post.Photo)) return 'photo'
  return 'post'
}

const KIND_VERB = {
  video: 'uploaded a video',
  story: 'posted a story',
  photo: 'shared a photo',
  post: 'posted an update',
}

/* ------------------------------------------------------------------
   Requests
   ------------------------------------------------------------------ */
const fetchActivity = (uuid) => {
  if (activityCache.has(uuid)) return Promise.resolve(activityCache.get(uuid))

  const base = process.env.REACT_APP_API_SERVER

  const posts = axios
    .get(base + 'manager/dashboard/userposts/' + uuid, auth())
    .then((res) => rows(res.data))
    .catch(() => null)

  const challenges = axios
    .get(base + 'manager/dashboard/userchallenges/' + uuid, auth())
    .then((res) => rows(res.data))
    .catch(() => null)

  return Promise.all([posts, challenges]).then(([p, c]) => {
    // Both halves failed: this player is skipped, and nothing is cached so a
    // later visit can try again. The rest of the feed still renders.
    if (p === null && c === null) return null
    const record = { posts: p || [], challenges: c || [] }
    activityCache.set(uuid, record)
    return record
  })
}

/*
 * Run `worker` over `items` with at most `limit` in flight. Lanes pull from a
 * shared cursor, so a slow player never blocks the queue behind it.
 */
const runPooled = (items, limit, worker) => {
  const out = new Array(items.length)
  let cursor = 0

  const lane = () => {
    const index = cursor
    cursor += 1
    if (index >= items.length) return Promise.resolve()
    return Promise.resolve()
      .then(() => worker(items[index], index))
      .then((value) => { out[index] = value })
      .catch(() => { out[index] = null })
      .then(lane)
  }

  const lanes = []
  for (let i = 0; i < Math.min(limit, items.length); i += 1) lanes.push(lane())
  return Promise.all(lanes).then(() => out)
}

/* ------------------------------------------------------------------
   Icons
   ------------------------------------------------------------------ */
const IconVideo = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <rect x="2.5" y="6" width="13" height="12" rx="2.5" />
    <path d="M15.5 10.5l5-3v9l-5-3z" />
  </svg>
)

const IconPhoto = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <rect x="3" y="4.5" width="18" height="15" rx="2.5" />
    <circle cx="8.5" cy="10" r="1.6" />
    <path d="M4 17l5-4.5 4 3.5 3-2.5 4 3.5" />
  </svg>
)

const IconPost = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M20 14.5a2.5 2.5 0 0 1-2.5 2.5H8l-4 3.5V6.5A2.5 2.5 0 0 1 6.5 4h11A2.5 2.5 0 0 1 20 6.5z" />
  </svg>
)

const IconStory = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="12" cy="12" r="8.5" />
    <circle cx="12" cy="12" r="3.5" />
  </svg>
)

const IconChallenge = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M7 4h10v5a5 5 0 0 1-10 0z" />
    <path d="M7 5.5H4.5V8a3 3 0 0 0 3 3M17 5.5h2.5V8a3 3 0 0 1-3 3" />
    <path d="M12 14v3.5M8.5 20h7" />
  </svg>
)

const MARK = {
  video: IconVideo,
  photo: IconPhoto,
  story: IconStory,
  post: IconPost,
  challenge: IconChallenge,
}

/* ------------------------------------------------------------------
   PlayerAvatar — a thin size preset over the shared ui/Avatar.
   The photo/404/initials handling all lives there now; the only thing this
   knows is how big a face is on this page: 36px in the watchlist table (the
   width of that grid column), 30px on a feed row. Kept as a named export so
   FavouritePlayers and anything else importing it keeps compiling.
   ------------------------------------------------------------------ */
export function PlayerAvatar({ uuid, name, small }) {
  return <Avatar uuid={uuid} name={name} size={small ? 30 : 36} />
}

/* ------------------------------------------------------------------
   Feed
   ------------------------------------------------------------------ */
/*
 * players  — the watchlist rows: [{UUID, Name, Position, Country, v}]
 * loading  — optional. True while the caller is still fetching that list, so the
 *            feed shows skeletons instead of flashing "nothing here" for a frame.
 */
export default function WatchlistFeed({ players, loading }) {
  const watched = Array.isArray(players) ? players.filter((p) => p && p.UUID) : []
  const key = watched.map((p) => p.UUID).join(',')

  const [dated, setDated] = useState([])
  const [undated, setUndated] = useState([])
  const [fetching, setFetching] = useState(watched.length > 0)
  const [reached, setReached] = useState(0)

  const isLoading = Boolean(loading) || fetching

  const aliveRef = useRef(true)
  const runRef = useRef(0)

  useEffect(() => () => { aliveRef.current = false }, [])

  useEffect(() => {
    runRef.current += 1
    const run = runRef.current

    if (watched.length === 0) {
      setDated([])
      setUndated([])
      setReached(0)
      setFetching(false)
      return
    }

    setFetching(true)

    runPooled(watched, LANES, (player) => (
      fetchActivity(player.UUID).then((record) => ({ player, record }))
    )).then((results) => {
      if (!aliveRef.current || run !== runRef.current) return

      const timeline = []
      const loose = []
      let ok = 0

      results.forEach((result) => {
        if (!result || !result.record) return // this player's requests failed
        ok += 1

        const player = result.player
        const who = shortName(player.Name)
        const shared = {
          uuid: player.UUID,
          name: player.Name,
          who,
          position: player.Position,
          points: player.v,
        }

        result.record.posts.forEach((post, index) => {
          const kind = postKind(post)
          const when = dateFrom(post, POST_DATE_FIELDS)
          const entry = Object.assign({}, shared, {
            id: player.UUID + '-p-' + (post.id !== undefined ? post.id : index),
            kind,
            action: KIND_VERB[kind],
            detail: snippet(post.Title || post.Descr),
            when,
          })
          // A post with no readable date is dropped rather than dated by guess.
          if (when) timeline.push(entry)
        })

        result.record.challenges.forEach((chal, index) => {
          const when = dateFrom(chal, CHALLENGE_DATE_FIELDS)
          const closes = toMoment(chal.DateTo)
          const title = clean(chal.Title)
          const entry = Object.assign({}, shared, {
            id: player.UUID + '-c-' + (chal.id !== undefined ? chal.id : (chal.ChallengeID || index)),
            kind: 'challenge',
            action: title ? 'entered ' + title : 'entered a challenge',
            detail: Number(chal.Points) > 0 ? Number(chal.Points) + ' pts on offer' : '',
            when,
            closes,
          })
          if (when) timeline.push(entry)
          else loose.push(entry)
        })
      })

      timeline.sort((a, b) => b.when.valueOf() - a.when.valueOf())

      setDated(timeline.slice(0, MAX_FEED))
      setUndated(loose.slice(0, MAX_UNDATED))
      setReached(ok)
      setFetching(false)
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  const total = dated.length + undated.length
  const plural = (n, one, many) => (n === 1 ? one : many)

  const subtitle = isLoading
    ? 'Reading the watchlist…'
    : (watched.length === 0
      ? 'Posts and challenges from the players you follow'
      : total + ' ' + plural(total, 'entry', 'entries') + ' from ' + reached + ' ' + plural(reached, 'player', 'players'))

  const row = (entry, showRelative) => {
    const Mark = MARK[entry.kind] || IconPost

    return (
      <div className="wf-row" key={entry.id}>
        <span className={'wf-mark wf-mark-' + entry.kind}><Mark /></span>

        <Link className="wf-face" to={'/user/' + entry.uuid} aria-label={entry.name}>
          <PlayerCard
            uuid={entry.uuid}
            name={entry.name}
            position={entry.position}
            points={entry.points}
          >
            <PlayerAvatar uuid={entry.uuid} name={entry.name} small />
          </PlayerCard>
        </Link>

        <div className="wf-body">
          <p className="wf-line"><b>{entry.who}</b> {entry.action}</p>
          {entry.detail ? <p className="wf-detail">{entry.detail}</p> : null}
        </div>

        {showRelative && entry.when ? (
          <time
            className="wf-when mono"
            dateTime={entry.when.toISOString()}
            title={entry.when.format('D MMM YYYY, HH:mm')}
          >
            {entry.when.fromNow()}
          </time>
        ) : null}

        {!showRelative && entry.closes ? (
          <span className="wf-when wf-closes mono" title="Challenge closing date — not an activity date">
            closes {entry.closes.format('D MMM')}
          </span>
        ) : null}
      </div>
    )
  }

  return (
    <Panel title="Watchlist Activity" subtitle={subtitle}>
      {isLoading && (
        <div className="wf-skeleton">
          <div className="skeleton wf-sk"></div>
          <div className="skeleton wf-sk"></div>
          <div className="skeleton wf-sk"></div>
          <div className="skeleton wf-sk wf-sk-short"></div>
        </div>
      )}

      {!isLoading && watched.length === 0 && (
        <div className="empty">Activity appears here once you are watching players.</div>
      )}

      {!isLoading && watched.length > 0 && total === 0 && (
        <div className="empty">
          Nothing from your watchlist yet. Posts and challenge entries show up here as they happen.
        </div>
      )}

      {!isLoading && total > 0 && (
        <div className="wf-list scroll-body wf-scroll">
          {dated.map((entry) => row(entry, true))}

          {undated.length > 0 && (
            <div className="wf-section">
              <span className="mono wf-section-label">Recent challenges</span>
              <span className="wf-section-note">No entry date recorded for these</span>
            </div>
          )}

          {undated.map((entry) => row(entry, false))}
        </div>
      )}
    </Panel>
  )
}
